/**
 * The rave card — ONE design, used by My raves and by All raves.
 *
 * The two screens had drifted into two cards for the same object: different
 * wash, different heading font, counters on one and a date range on the other,
 * and an `Attend` button that stayed `Attend` for a rave you were already
 * going to. A person moving between the two had to re-learn the same thing.
 *
 * The card takes a `Rave` and renders one shape. What varies is what the data
 * says — artwork or no artwork, attending or not, over or still to come — not
 * which screen is asking.
 *
 * **Card view only.** The list view is `RaveTable`, a real table: browsing and
 * comparing are different jobs, and this card laid on its side did neither
 * well.
 */
import { guardedNavigation, leaveTo } from '@/app/AuthProvider';
import type { ReactNode } from 'react';
import { Button, Card, Row, SrOnly, Stack, Txt, cx, localDateRange } from '@/ui';
import { festivalUrl } from '@/lib/tenant';
import { RaveAdminGear } from '@/app/RaveAdminGear';
import { useLocale } from '@/app/LocaleProvider';
import { useT, type Vars } from '@/i18n';
import { intlLocale } from '@/app/rich';
import { formatDateRange } from '@myrave/shared';
import './RaveCard.css';

type Tr = (english: string, vars?: Vars) => string;

export interface Rave {
  editionId: string;
  /**
   * The edition's slug — its year in the address (`ozora.myrave.io/2026`).
   * Every link to this rave uses it, so a card for 2026 opens 2026 whatever
   * the bare subdomain opens by then. Absent only for a caller that does not
   * know it, and then the link is the festival's default.
   */
  editionSlug?: string | null;
  editionName: string;
  festivalName: string;
  subdomain: string;
  venue?: string | null;
  country?: string | null;
  /** How many people come (public sources, not MyRave's users) — Find a rave's rows add it up. */
  crowdSize?: number | null;
  /** A TEST rave — a demo copy (OZORA 2222 and its kind): a TEST strip across its cover (2026-10-26). */
  test?: boolean;
  /** What kind of rave — `RAVE_TYPES` slugs; Find a rave's rows and filter in the phone app. */
  types?: readonly string[];
  timezone?: string;
  gatesOpenAt?: string | null;
  endsAt?: string | null;
  /** Artwork. Either may be absent; the card copes with both. */
  logoUrl?: string | null;
  coverUrl?: string | null;
  /**
   * The size of the published lineup, from the API — "664 acts · 24 stages".
   * Absent or 0 when nothing is published yet, and then the card says nothing
   * rather than "0 acts", which is true and reads as broken.
   */
  actCount?: number;
  stageCount?: number;
  /** Present only for a rave the person attends. */
  saved?: number;
  clashes?: number;
  plannedMinutes?: number;
  attending?: boolean;
  /** You hold a grant on it (festival admin or content manager) — in My raves whether or not you attend. */
  managed?: boolean;
  /** `false` when the rave runs no countdown module: no pill. Absent means on. */
  countdown?: boolean;
}

/** `days` is set only for `soon`, and is what its label counts. */
export type Timing = { tone: 'live' | 'soon' | 'past' | 'tbc'; days?: number };

/**
 * When the rave is, derived from the instant and never stored (hard rule 8).
 *
 * Day resolution on purpose: the app bar already ticks a live countdown, and a
 * second one on every card is motion competing for attention on a page meant
 * to be scanned.
 */
export function timingOf(r: Rave, now: number): Timing {
  if (!r.gatesOpenAt) return { tone: 'tbc' };
  const gates = new Date(r.gatesOpenAt).getTime();
  const ends = r.endsAt ? new Date(r.endsAt).getTime() : gates;
  if (now >= gates && now <= ends) return { tone: 'live' };
  if (now > ends) return { tone: 'past' };
  return { tone: 'soon', days: Math.max(1, Math.ceil((gates - now) / 86_400_000)) };
}

/** What a `Timing` says, in the reader's language. */
export function timingLabel(t: Timing, tr: Tr): string {
  switch (t.tone) {
    case 'tbc': return tr('Dates to be announced');
    case 'live': return tr('On now');
    case 'past': return tr('Ended');
    default: return (t.days ?? 1) <= 1 ? tr('Tomorrow') : tr('In {days} days', { days: t.days ?? 1 });
  }
}

/** `330` → `5h 30m`. Minutes alone stop reading after about two hours. */
export function duration(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * The festival's logo, painted as a MASK rather than an `<img>`.
 *
 * OZORA's mark is a path set declaring `fill="currentColor"`, drawn to be
 * tinted by its host. Inside an `<img>` that colour never arrives — a
 * standalone SVG resolves `currentColor` against its own root, which defaults
 * to black — so it rendered black on a near-black card. Step 49 found that by
 * looking at the screen, not by testing.
 *
 * A cross-origin CSS mask also needs `Access-Control-Allow-Origin`, which the
 * media route sends; CORP alone is enough for an `<img>` and not for this.
 */
function Logo({ url, name, height = 28 }: { url: string; name: string; height?: number }) {
  // The START edge — the name it replaces is start-aligned, and the card
  // should not shift when a festival happens to have artwork. `mask-position`
  // has no logical keyword, so the direction is asked for.
  const { direction } = useLocale();
  return (
    <span
      role="img"
      aria-label={name}
      className={cx('rave-card-logo', direction === 'rtl' && 'is-rtl')}
      style={{ height, maskImage: `url(${url})`, WebkitMaskImage: `url(${url})` }}
    />
  );
}

/**
 * The corner ribbon: attending, or over.
 *
 * Both say a WORD. A coloured corner alone is not readable in sunlight and not
 * readable at all to plenty of people, and these two states are the ones a
 * person scans a grid for.
 */
function Ribbon({ kind }: { kind: 'attending' | 'ended' | 'live' }) {
  const tr = useT();
  const attending = kind === 'attending';
  const live = kind === 'live';
  return (
    <span className={cx('rave-card-ribbon', attending && 'is-attending', live && 'is-live')}>
      {attending ? (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5 9 17.5 20 6.5" /></svg>
      ) : (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      )}
      {attending ? tr('ATTENDING') : live ? tr('ON NOW') : tr('ENDED')}
    </span>
  );
}

/** Saved acts and clashes, as icon + number. Only for a rave being attended. */
function MiniStats({ r, onDark }: { r: Rave; onDark?: boolean }) {
  const tr = useT();
  if (r.saved === undefined) return null;
  const clashes = r.clashes ?? 0;
  // Over artwork everything goes white with a shadow; the tinted icons stay
  // tinted, because that is the one thing the colour is doing.
  return (
    <Row gap={12} className="rave-card-stats-row">
      <Row gap={5}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={cx('rave-card-icon', 'rave-card-heart', onDark && 'is-lifted')}>
          <path d="M12 21s-8-5.1-8-10.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8 3.7C20 15.9 12 21 12 21z" />
        </svg>
        <Txt size={12.5} weight={700} tabular className={cx('rave-card-ink', onDark && 'is-dark')}>{r.saved}</Txt>
        <SrOnly>{tr('saved')}</SrOnly>
      </Row>
      <Row gap={5}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cx('rave-card-icon', 'rave-card-clash-icon', onDark && 'is-lifted is-dark', clashes > 0 && 'is-bad')}>
          <path d="M12 9v4M12 17v.01M4 20h16L12 4z" />
        </svg>
        <Txt size={12.5} weight={700} tabular className={cx('rave-card-ink', onDark && 'is-dark', clashes > 0 && 'is-bad')}>{clashes}</Txt>
        <SrOnly>{tr('clashes')}</SrOnly>
      </Row>
    </Row>
  );
}

/**
 * "24 July – 4 August 2026", or what is actually known.
 *
 * A start with no end is the normal state for an edition announced early —
 * saying "Dates to be announced" when the gates time is right there reads as
 * the app having lost it.
 */
function dates(r: Rave, tr: Tr, locale: string): string {
  const tz = r.timezone ?? 'UTC';
  if (r.gatesOpenAt && r.endsAt) {
    return localDateRange(locale, formatDateRange(new Date(r.gatesOpenAt), new Date(r.endsAt), tz), new Date(r.gatesOpenAt), new Date(r.endsAt), tz);
  }
  if (r.gatesOpenAt) {
    return tr('From {date}', {
      date: new Intl.DateTimeFormat(intlLocale(locale), {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: tz,
      }).format(new Date(r.gatesOpenAt)),
    });
  }
  return tr('Dates to be announced');
}

/**
 * "664 acts · 24 stages", or nothing while no lineup is published.
 *
 * Shared with `RaveTable`, so the card and the row cannot count differently.
 */
export function lineupLine(r: Rave, tr: Tr): string | null {
  const parts: string[] = [];
  const acts = r.actCount ?? 0;
  const stages = r.stageCount ?? 0;
  if (acts > 0) parts.push(acts === 1 ? tr('1 act') : tr('{count} acts', { count: acts }));
  if (acts > 0 && stages > 0) parts.push(stages === 1 ? tr('1 stage') : tr('{count} stages', { count: stages }));
  return parts.length ? parts.join(' · ') : null;
}

function place(r: Rave): string | null {
  return [r.venue, r.country].filter(Boolean).join(' · ') || null;
}

export function RaveCard({
  rave, now, onAttend, busy, onCountdown, onOpen, openLabel, showCrowd,
}: {
  /**
   * How many people come, "🕺 ~30K" (the user, 2026-10-26: "show the crowd number with ~ on each
   * card, maybe dancing emojis") — Find a rave's rows in the phone app; the website's cards stay.
   */
  showCrowd?: boolean;
  rave: Rave;
  now: number;
  onAttend?: () => void;
  busy?: boolean;
  /** Open the countdown over the grid. Absent on a list that has no panel. */
  onCountdown?: (rave: Rave) => void;
  /**
   * Open it HERE instead of going to the festival's own app.
   *
   * The rave admin's dashboard is this card; its action leads to a settings
   * page on `radm.`, not to the rave. Absent everywhere else, so the card
   * keeps being a link to the festival.
   */
  onOpen?: () => void;
  openLabel?: string;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const t = timingOf(rave, now);
  const href = festivalUrl(rave.subdomain, rave.editionSlug);

  /*
   * Days and hours, not seconds.
   *
   * A grid holds a dozen of these and a ticking seconds field in every tile
   * is a dozen re-renders a second for a number nobody reads at that size.
   * `now` is already read once per render by the list; the pill is coarse on
   * purpose and the full clock is one tap away.
   */
  const countdownTo = (() => {
    if (!rave.gatesOpenAt || rave.countdown === false) return null;
    const ms = new Date(rave.gatesOpenAt).getTime() - now;
    if (ms <= 0) return null;
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor((ms % 86_400_000) / 3_600_000);
    return days > 0 ? tr('{days}d {hours}h', { days, hours }) : tr('{hours}h', { hours });
  })();

  const head = (
    <a href={href} onClick={guardedNavigation(href)} className="rave-card-link">
      {/*
        * The cover, or the atmosphere in its place.
        *
        * No festival has uploaded one yet (`coverKey` is null across the
        * fixture), so the fallback is not an edge case — it is what every card
        * shows today, and it has to look deliberate rather than broken.
        */}
      <div className={rave.coverUrl ? 'rave-card-cover-plate' : 'rave-card-cover-wash'}>
        {rave.test && <div className="rave-card-test">{tr('Test')}</div>}
        {rave.coverUrl && (
          <img
            src={rave.coverUrl}
            alt=""
            loading="lazy"
            /*
             * `cover` for a photograph, `contain` for a MARK — decided from
             * the file, because the two want opposite things and the URL does
             * not say which it is.
             *
             * OZORA's cover is its app icon: a square logo on black. Cropped
             * to 16:9 the mark lost its top and bottom and read as a broken
             * image. A photograph contained instead would letterbox. Measuring
             * the natural size on load is what tells them apart.
             */
            onLoad={(e) => {
              const img = e.currentTarget;
              const ratio = img.naturalWidth / Math.max(1, img.naturalHeight);
              const isMark = ratio > 0.8 && ratio < 1.25;
              img.style.objectFit = isMark ? 'contain' : 'cover';
              /*
               * A contained mark leaves bars either side, and OZORA's icon
               * carries its own black ground — so the plate goes black too and
               * the seam disappears. Without this the PNG read as a black
               * square pasted onto a lighter card.
               */
              if (isMark && img.parentElement) img.parentElement.style.background = 'var(--black)';
            }}
            className="rave-card-cover"
          />
        )}
        {/*
          * Both, when both are true. A rave you attended that is now over is
          * a real and common state — showing only one of the two would hide
          * whichever the person was scanning for.
          */}
        {/*
          * Pushed below the corner when the countdown is up there.
          *
          * Two cards across a 390px phone is ~170px of artwork. The pill
          * ("302d 10h") takes ~105 of it, so ATTENDING ran underneath the
          * countdown and neither could be read. They cannot share a line at
          * that width, and a ribbon that is sometimes truncated is worse than
          * one that is always a line lower.
          *
          * The gear ALONE is only 32px and leaves room for a ribbon beside
          * it, so it does not push anything down.
          */}
        <Row gap={6} className={cx('rave-card-ribbons', !!countdownTo && 'is-low')}>
          {rave.attending && <Ribbon kind="attending" />}
          {t.tone === 'past' && <Ribbon kind="ended" />}
          {t.tone === 'live' && <Ribbon kind="live" />}
        </Row>

        {/*
          * The counters sit ON the artwork, along its bottom edge.
          *
          * They belong to the rave the picture is of, and putting them in the
          * text block below pushed the name, dates and place down a line each.
          * The gradient is what keeps them readable: a cover is somebody
          * else's photograph and may be bright exactly where these land.
          */}
        {rave.saved !== undefined && (
          <div className="rave-card-stats-scrim">
            <MiniStats r={rave} onDark />
          </div>
        )}
      </div>
    </a>
  );

  /*
   * The countdown pill, OUTSIDE the card's link.
   *
   * It was nested inside the `<a>`, and a `<button>` inside an anchor is
   * invalid HTML: the parser lifts it out of the element React thinks it is
   * in, and the click handler silently stops firing. It measured as a perfect
   * button that did nothing.
   *
   * So it is a sibling, positioned over the artwork by the card itself.
   */
  const countdownPill = countdownTo ? (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCountdown?.(rave); }}
      aria-label={tr('Countdown to {name}', { name: rave.editionName })}
      className="rave-card-countdown"
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2M9 2h6" />
      </svg>
      {countdownTo}
    </button>
  ) : null;

  const identity = (
    <a href={href} onClick={guardedNavigation(href)} className="rave-card-link">
      <Stack gap={5}>
        {/* The logo REPLACES the name where there is one — and still carries
            it as the accessible label, so nothing is lost to a screen reader.
            The EDITION's name ("O.Z.O.R.A 2027"): each year is its own card,
            and two cards both reading "OZORA" could not be told apart. */}
        {rave.logoUrl
          ? <Logo url={rave.logoUrl} name={rave.editionName} />
          : <Txt size={16} weight={800} track={-0.01}>{rave.editionName}</Txt>}
        <Txt size={12} tone="dim" weight={600}>{dates(rave, tr, locale)}</Txt>
        {place(rave) && <Txt size={11.5} tone="muted" className="rave-card-place">{place(rave)}</Txt>}
        {lineupLine(rave, tr) && <Txt size={11.5} tone="muted" tabular>{lineupLine(rave, tr)}</Txt>}
      </Stack>
    </a>
  );

  /*
   * Attend and Go are deliberately DIFFERENT colours.
   *
   * "Attend" is the accent — the thing to do on a rave you have not chosen.
   * "Open rave app" is the mint used for a settled, already-yours state, so a
   * grid tells you which is which before either label is read. It says what it
   * does: the festival's own application, on its own subdomain.
   *
   * **There is no "leave" here.** Leaving discards a plan somebody built, and
   * a card in a grid is the wrong place for that: it sits one thumb-width from
   * "Go to the rave", with no undo behind it. It lives inside the rave, where
   * the person can see what they would be throwing away.
   */
  const action = onOpen ? (
    /* The admin dashboard's card: it opens a settings page here, not the
       festival's application. */
    <Button variant="go" full onClick={onOpen}>
      {openLabel ?? tr('Open')}
    </Button>
  ) : rave.attending ? (
    <Button variant="go" full onClick={() => void leaveTo(href)}>
      {tr('Open rave app')}
    </Button>
  ) : onAttend ? (
    <Button variant="primary" full loading={busy} loadingLabel={tr('Adding…')} onClick={onAttend}>
      {tr('Attend')}
    </Button>
  ) : null;

  return (
    // The card is `position: relative`: the pill positions against it, because
    // it is a sibling of the artwork's link rather than a child of it.
    <Card className="rave-card-card">
      {head}

      {/*
        * The top-END corner, holding the countdown and the gear.
        *
        * They were both laying claim to it — the pill positioned itself
        * absolutely and the gear was asked for in the same corner — so one
        * row owns it and they cannot overlap at any width. The gear is LAST,
        * so it lands in the very corner and stays in the same place whether
        * or not the rave has a countdown; a control that moves depending on
        * the data is a control people have to look for.
        *
        * Outside the artwork's `<a>`: a `<button>` nested in an anchor is
        * invalid HTML, and the parser lifts it out of the element React
        * thinks it is in — which is how the countdown measured as a perfect
        * button that did nothing.
        */}
      {(countdownPill || !onOpen) && (
        <Row gap={6} className="rave-card-corner">
          {countdownPill}
          {/*
            * NOT on the admin dashboard's own card — `onOpen` means this card
            * is already inside an admin and its button already says Manage.
            */}
          {!onOpen && <RaveAdminGear editionId={rave.editionId} size={32} onArtwork />}
        </Row>
      )}
      <Stack gap={10} className="rave-card-body">
        {identity}
        {showCrowd && typeof rave.crowdSize === 'number' && rave.crowdSize > 0 && ((n) => (
          <span className="rave-card-crowd" aria-label={tr('~{count} ravers', { count: n })}>
            <span aria-hidden="true">🕺 ~{n}</span>
          </span>
        ))(new Intl.NumberFormat(intlLocale(locale), { notation: 'compact', maximumFractionDigits: 1 }).format(rave.crowdSize))}
        {/* `marginBlockStart: auto` pins the action to the bottom, so a grid
            of cards with different amounts of text still lines its buttons up. */}
        <div className="rave-card-foot">{action}</div>
      </Stack>
    </Card>
  );
}

/**
 * The grid: three across on a wide screen, two on a phone, never one.
 *
 * Fixed columns rather than `auto-fit`, because the count is the requirement —
 * `auto-fit` derives columns from a minimum width and would drop to one card
 * on a narrow phone, which is the layout this replaces.
 */
export function RaveGrid({ children }: { children: ReactNode }) {
  return <div className="rave-grid">{children}</div>;
}
